/**
 * useRequestMagicLink Hook
 *
 * Requests a magic link/code for email login using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const RequestMagicLinkDocument = `
  mutation RequestMagicLink($email: String!) {
    requestMagicLink(email: $email) {
      sent
      reason
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface MagicLinkResponse {
  sent: boolean;
  reason: string | null;
}

interface UseRequestMagicLinkResult {
  requestMagicLink: (email: string) => Promise<MagicLinkResponse>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Request a magic link/code for email login.
 */
export function useRequestMagicLink(): UseRequestMagicLinkResult {
  const [result, executeMutation] = useMutation<
    { requestMagicLink: MagicLinkResponse },
    { email: string }
  >(RequestMagicLinkDocument);

  const requestMagicLink = useCallback(
    async (email: string): Promise<MagicLinkResponse> => {
      try {
        const response = await executeMutation({
          email: email.toLowerCase().trim(),
        });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to request magic link"
          );
        }

        if (!response.data?.requestMagicLink) {
          throw new Error("No response from server");
        }

        return response.data.requestMagicLink;
      } catch (err) {
        const linkError =
          err instanceof Error ? err : new Error("Failed to request magic link");

        errorTracking.captureError(linkError, {
          tags: { feature: "auth", action: "requestMagicLink" },
        });

        throw linkError;
      }
    },
    [executeMutation]
  );

  return {
    requestMagicLink,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
